/**
 * gldn-makers-grid — tag filter, name search, and card detail navigation.
 *
 * Ported from the Simplistic theme's jQuery `employees_list` section. Behavior
 * is deliberately breakpoint-split, matching the legacy `large--` /
 * `medium-down--` class pairs:
 *
 *   desktop (>= 990px) — CSS :hover reveals the whole detail face. No JS.
 *   mobile  (<  990px) — tapping a card reveals ONE detail pane at a time,
 *                        navigated with prev/next cursors and dismissed with
 *                        the close button.
 *
 * Filtering hides list items rather than removing them, so the grid's reserved
 * tile boxes keep the page from shifting.
 */

const DESKTOP_QUERY = '(min-width: 990px)';
const EVERYONE = 'everyone';

/** Split a comma-separated tag attribute into trimmed, lowercase tags. */
function parseTags(value) {
  return String(value || '')
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

class MakersGrid {
  constructor(root) {
    this.root = root;
    this.filters = root.querySelector('[data-tag-bar]');
    this.searchInput = root.querySelector('[data-maker-search]');
    this.headerLabel = root.querySelector('[data-tags-header-label]');
    this.overflowGroup = root.querySelector('[data-tags-overflow]');
    this.emptyMessage = root.querySelector('[data-maker-empty]');
    this.items = Array.from(root.querySelectorAll('[data-maker-item]'));
    this.tagButtons = Array.from(root.querySelectorAll('[data-tag-filter]'));

    this.activeTag = EVERYONE;
    this.query = '';

    this.bind();
    this.apply();
  }

  bind() {
    this.tagButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activeTag = (button.dataset.tagFilter || EVERYONE).toLowerCase();
        this.query = '';
        if (this.searchInput) this.searchInput.value = '';
        this.setHeaderLabel(button.textContent.trim());
        this.closeFilters();
        this.apply();
      });
    });

    if (this.searchInput) {
      // Legacy searched on the enter key and on the icon click. Filtering as
      // you type is strictly better and costs nothing here.
      this.searchInput.addEventListener('input', () => {
        this.query = this.searchInput.value.trim().toLowerCase();
        if (this.query !== '') this.activeTag = EVERYONE;
        this.apply();
      });
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
    }

    const header = this.root.querySelector('[data-tags-header]');
    if (header) {
      header.addEventListener('click', () => {
        const open = this.filters?.classList.toggle('is-open');
        header.setAttribute('aria-expanded', String(Boolean(open)));
      });
    }

    const more = this.root.querySelector('[data-tags-more]');
    if (more && this.overflowGroup) {
      more.addEventListener('click', () => {
        const open = this.overflowGroup.classList.toggle('is-open');
        more.setAttribute('aria-expanded', String(open));
      });
    }

    this.root.querySelectorAll('[data-maker-card]').forEach((card) => new MakerCard(card));
  }

  setHeaderLabel(label) {
    if (this.headerLabel) this.headerLabel.textContent = label;
  }

  closeFilters() {
    this.filters?.classList.remove('is-open');
    this.root.querySelector('[data-tags-header]')?.setAttribute('aria-expanded', 'false');
  }

  matches(item) {
    if (this.query !== '') {
      return String(item.dataset.name || '').toLowerCase().includes(this.query);
    }
    if (this.activeTag === EVERYONE) return true;
    return parseTags(item.dataset.tags).includes(this.activeTag);
  }

  apply() {
    let visible = 0;
    this.items.forEach((item) => {
      const show = this.matches(item);
      item.hidden = !show;
      if (show) visible += 1;
    });

    this.tagButtons.forEach((button) => {
      const tag = (button.dataset.tagFilter || '').toLowerCase();
      button.classList.toggle('is-selected', this.query === '' && tag === this.activeTag);
    });

    if (this.emptyMessage) this.emptyMessage.hidden = visible !== 0;
  }
}

class MakerCard {
  constructor(card) {
    this.card = card;
    this.panes = Array.from(card.querySelectorAll('[data-card-pane]'));
    this.index = 0;
    this.desktop = window.matchMedia(DESKTOP_QUERY);

    card.querySelector('[data-card-toggle]')?.addEventListener('click', () => this.toggle());
    card.querySelector('[data-card-close]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.close();
    });

    card.querySelectorAll('[data-card-move]').forEach((cursor) => {
      cursor.addEventListener('click', (event) => {
        event.stopPropagation();
        this.move(Number(cursor.dataset.cardMove) || 0);
      });
    });

    // Leaving the mobile breakpoint mid-interaction would otherwise strand a
    // card in its toggled state with only one pane visible.
    this.desktop.addEventListener('change', () => this.close());
  }

  toggle() {
    if (this.card.classList.contains('is-toggled')) {
      this.close();
      return;
    }
    this.setPane(0);
    this.card.classList.add('is-toggled');
    this.card.querySelector('[data-card-toggle]')?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.setPane(0);
    this.card.classList.remove('is-toggled');
    this.card.querySelector('[data-card-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  move(delta) {
    if (this.panes.length === 0) return;
    this.setPane((this.index + delta + this.panes.length) % this.panes.length);
  }

  setPane(index) {
    this.index = index;
    this.panes.forEach((pane, i) => pane.classList.toggle('is-active', i === index));
  }
}

function init(scope = document) {
  scope.querySelectorAll('[data-gldn-makers-grid]').forEach((root) => {
    if (root.dataset.makersGridReady === 'true') return;
    root.dataset.makersGridReady = 'true';
    new MakersGrid(root);
  });
}

init();

// Theme editor re-renders the section; re-bind the fresh DOM.
document.addEventListener('shopify:section:load', (event) => init(event.target));
